import axios, { AxiosInstance, AxiosRequestConfig } from 'axios';
import { getUserData } from '../redux/slice/slice.auth';
import ENVIRONMENT from '../config/environment';
import { logger } from '@/utils/logger';

export interface ServiceApiError {
	status: boolean;
	message: string;
	data: unknown | null;
	statusCode?: number;
}

class ServiceApi {
	private _axios: AxiosInstance;
	private static _instance: ServiceApi | null = null;

	private constructor(baseUrl: string, token: string | null = null) {
		this._axios = axios.create({
			baseURL: baseUrl,
			// validateStatus: () => true, This prevents Axios from rejecting responses based on HTTP status codes (e.g., 404, 500). Normally, Axios rejects status codes outside of 2xx.
			responseType: 'json',
			withCredentials: false, //Requests won't include cookies/auth headers by default.
		});

		this._axios.interceptors.request.use(config => {
			if (token) config.headers['Authorization'] = `Bearer ${token}`;
			logger.debug(`[axios] Sending API request`, {
				url: config.url,
				method: config.method,
				params: config.params,
			});
			return config;
		}, Promise.reject);

		this._axios.interceptors.response.use(
			response => response,
			error => {
				if (error.response?.status === 401) {
					localStorage.clear();
					window.location.reload();
				}
				return Promise.reject(this.formatError(error));
			},
		);
	}

	// Standardize Error Formatting
	private formatError(error: any): ServiceApiError {
		if (axios.isAxiosError(error) && error.response) {
			return {
				status: error.response.data?.status ?? false,
				message: error.response.data?.message || error.message,
				data: error.response.data?.data || null,
				statusCode: error.response.status,
			};
		}
		// Handle network errors or unexpected issues
		return {
			status: false,
			message: error.message || 'An unexpected error occurred',
			data: null,
			statusCode: error.response?.status || 500,
		};
	}

	public updateToken(token: string | null) {
		if (token) {
			this._axios.defaults.headers['Authorization'] = `Bearer ${token}`;
		} else {
			delete this._axios.defaults.headers['Authorization'];
		}
	}

	public static destroyInstance() {
		if (this._instance) {
			this._instance._axios = {} as AxiosInstance; // reset axios
		}
		this._instance = null; // reset instance
	}

	public static getInstance(
		instanceFor?: 'DS' | 'OS',
		baseUrl: string | null = null,
	): ServiceApi {
		const token =
			instanceFor === 'DS' ? ENVIRONMENT.DS_API_TOKEN : getUserData()?.token;
		const resolvedBaseUrl =
			baseUrl ||
			(instanceFor === 'DS'
				? ENVIRONMENT.DS_API_URL_BUYER_CREDIT
				: ENVIRONMENT.API_URL);

		if (
			!this._instance ||
			this._instance._axios.defaults.baseURL !== resolvedBaseUrl ||
			this._instance._axios.defaults.headers['Authorization'] !== `Bearer ${token}`
		) {
			this._instance = new ServiceApi(resolvedBaseUrl, token);
		}

		if (this._instance && token) {
			this._instance.updateToken(token);
		}

		return this._instance;
	}

	public GET(url: string, config?: AxiosRequestConfig) {
		return this._axios.get(url, config);
	}

	public POST(url: string, data: any, config?: AxiosRequestConfig) {
		return this._axios.post(url, data, config);
	}

	public PUT(url: string, data: any, config?: AxiosRequestConfig) {
		return this._axios.put(url, data, config);
	}

	public PATCH(url: string, data: any, config?: AxiosRequestConfig) {
		return this._axios.patch(url, data, config);
	}

	public DELETE(url: string, config?: AxiosRequestConfig) {
		return this._axios.delete(url, config);
	}

	public Axios(url: string, config: AxiosRequestConfig) {
		return this._axios.request({ url, ...config });
	}
}

export default ServiceApi;
